/* eslint-disable no-unused-vars */

import { PortableText } from "@portabletext/react";
import { Button, IconDownloadFile } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMyAccount, useGetMyAccounts, useGetMyProfile,
  // useGetMyProfile,
  useGetSpotifyCount, useGetTopGigs, useGetTopWorks, useGetTopWriters } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
// import useSiteConfigUnAuth from "@/api/sanity/queries/getSiteConfigWithDomain";
import { hasAdminRights } from "@/auth/shared";
import ButtonDashboard from "@/components/ButtonDashboard";
import ChooseAccountModal from "@/components/ChooseAccountModal";
import DashboardOpportunitiesBlock from "@/components/DashboardOpportunitiesBlock";
import H1 from "@/components/H1";
import H2 from "@/components/H2";
import LoadingSmall from "@/components/LoadingSmall";
import SongSuggestionsModal from "@/components/SongSuggestionsModal.tsx";
import Spacer from "@/components/Spacer";
import TopGigs from "@/components/TopGigs";
import TopSongs from "@/components/TopSongs";
import TopWriters from "@/components/TopWriters";
import useGetColours from "@/helpers/getColours";
import GigClaimIconDark from "@/images/rebrandIcons/GigClaimIconDark.png";
import SongIconDark from "@/images/rebrandIcons/SongIconDark.png";
import WriterIconDark from "@/images/rebrandIcons/WriterIconDark.png";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

function Dashboard() {
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const flags = useFlags();
  const disableTopGigs = flags["pd-disable-top-gigs"];
  const spotifySuggestionsEnabled = flags["pd-spotify-suggestions-feature"];

  const myAccounts = useGetMyAccounts();

  const { data: siteConfig } = useSiteConfigAuth();
  // const { data: domainConfig } = useSiteConfigUnAuth();
  // const supportEmail = domainConfig?.support_email;
  const disableDashboard = siteConfig?.disable_dashboard;
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const isRoughTrade = siteConfig?.display_name?.includes(
    "Rough Trade Publishing",
  );

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
    accountname: selectedAccount,
  });

  const { highlightColour2 } = useGetColours(siteConfig);

  const adminRights = hasAdminRights(myAccount?.data);

  // Top works - gets top 100 works instead of individual songs
  const topSongs = useGetTopWorks({ enabled: myAccounts.isSuccess });
  const topWriters = useGetTopWriters({
    accounts: myAccounts?.data?.artistaccounts,
    enabled: myAccounts.isSuccess,
  });
  const topGigs = useGetTopGigs({ accounts: myAccounts?.data?.artistaccounts, enabled: myAccounts.isSuccess });

  const pendingSpotifyCount = useGetSpotifyCount({
    accounts: myAccounts?.data?.artistaccounts,
    enabled: myAccounts.isSuccess,
  });
  const navigate = useNavigate();
  const [accountModal, setAccountModal] = useState(null); // Single modal state
  const [songSuggestionsModalOpen, setSongSuggestionsModalOpen] = useState(false);

  const [addOportunityUrl, _setAddOportunityUrl] = useState(null);

  const { t } = useTranslation("dashboard");

  // Modal configurations
  const modalConfigs = {
    gig: {
      title: t("dashboard:choose-account-gigs-modal-header", "Select Account"),
      to: "/gigs/claim",
      nonAdminTo: "/gigs/",
    },
    statements: {
      title: "",
      to: "/statements",
      nonAdminTo: "/statements/",
    },
    opportunity: {
      title: t("dashboard:choose-account-modal-header", "Select Account"),
      to: addOportunityUrl,
      nonAdminTo: "/opportunities/",
    },
    writer: {
      title: t("dashboard:choose-account-writer-modal-header", "Select Account"),
      to: "/writers/create",
      nonAdminTo: "/writers/",
    },
    song: {
      title: t("dashboard:choose-account-songs-modal-header", "Select Account"),
      to: "/songs/create",
      nonAdminTo: "/songs/",
    },
  };

  const openAccountModal = (modalType) => {
    setAccountModal(modalType);
  };

  const closeAccountModal = () => {
    setAccountModal(null);
  };

  const viewStatements = () => {
    openAccountModal("statements");
  };

  useEffect(() => {
    if (isRoughTrade) {
      navigate({ to: "/statements" });
    }
  }, [isRoughTrade, navigate]);

  useEffect(() => {
    if (disableDashboard && !isRoughTrade) {
      navigate({ to: "/songs" });
    }
  }, [disableDashboard, navigate, isRoughTrade]);

  const { data: myProfile } = useGetMyProfile();

  // const hideRoyalties = profile?.data?.hideroyalties || myAccount?.data?.hideroyalties;

  return (
    <>
      {/* Single consolidated account modal */}
      {accountModal && modalConfigs[accountModal] && (
        <ChooseAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          title={modalConfigs[accountModal].title}
          to={modalConfigs[accountModal].to}
          nonAdminTo={modalConfigs[accountModal].nonAdminTo}
          setAccountChoiceModal={closeAccountModal}
          open={true}
        />
      )}

      {/* Song Suggestions Modal */}
      {spotifySuggestionsEnabled && (
        <SongSuggestionsModal
          open={songSuggestionsModalOpen}
          onClose={() => setSongSuggestionsModalOpen(false)}
          accounts={myAccounts?.data?.artistaccounts}
          totalSuggestionsCount={pendingSpotifyCount?.data?.[0]?.count}
        />
      )}

      <div className="flex flex-wrap">
        <div className="w-full lg:min-h-screen-minus-header">
          <div className="mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40">
            <H1>
              {t("dashboard:welcome-back", "Welcome Back")}
              {", "}
              {isTunecore ? myAccount?.data?.accountname : myProfile?.username}
              !
            </H1>
            <Spacer />
            <div className="flex flex-row flex-wrap justify-between gap-0 font-semibold lg:flex-nowrap lg:gap-6">
              {spotifySuggestionsEnabled && (
                <div
                  role="button"
                  onClick={() => setSongSuggestionsModalOpen(true)}
                  style={{
                    borderRadius: "10px 10px 10px 10px",
                    backgroundColor: !isTunecore ? highlightColour2 : undefined,
                  }}
                  className={`${
                    isTunecore
                      ? "bg-tunecore-dark-element text-white"
                      : "text-old-sentric-table-text"
                  } hover:shadow-lg lg:w-1/3 w-full h-44 flex flex-col justify-center gap-4 px-6 mb-6 ease-linear transition-all duration-150 hover:scale-105`}
                >
                  <div className="flex font-semibold text-center rounded">
                    {pendingSpotifyCount?.isSuccess
                      && pendingSpotifyCount?.data && (
                      <div>
                        <p className="text-3xl xl:text-4xl">
                          {pendingSpotifyCount.data?.[0]?.count}
                        </p>
                      </div>
                    )}
                    {pendingSpotifyCount?.isLoading && <LoadingSmall />}
                    {!pendingSpotifyCount?.isLoading
                      && !pendingSpotifyCount?.data && <span>-</span>}
                  </div>
                  <p className="text-lg font-medium text-left xl:text-xl">
                    {/* izzy's code */}
                    {t("dashboard:song-suggestions", "Song Suggestions")}
                  </p>
                </div>
              )}
              <div
                role="button"
                // onClick={() =>
                //   navigate({ to: "/writers" })}
                style={{
                  borderRadius: "10px 10px 10px 10px",
                  backgroundColor: !isTunecore ? highlightColour2 : undefined,
                }}
                className={`${
                  isTunecore
                    ? "bg-tunecore-dark-element text-white"
                    : "text-old-sentric-table-text"
                } hover:shadow-lg ${spotifySuggestionsEnabled ? "lg:w-1/3" : "lg:w-1/2"} w-full h-44 flex flex-col justify-center gap-4 px-6 mb-6 ease-linear transition-all duration-150 hover:scale-105 pointer-events-none`}
              >
                <div className="flex font-semibold text-center rounded">
                  {topWriters?.isLoading
                    ? <LoadingSmall />
                    : (
                        <span className="text-3xl xl:text-4xl">
                          {topWriters.data?.[0]?.verifieddate
                            ? format(
                                Date.parse(topWriters.data?.[0]?.verifieddate),
                                "yyyy-MM-dd",
                              )
                            : "-"}
                        </span>
                      )}
                </div>
                <p className="text-lg font-medium text-left xl:text-xl">
                  {t("dashboard:last-writer-added", "Last writer added")}
                </p>
              </div>
              <div
                role="button"
                // onClick={() =>
                //   navigate({ to: "/songs" })}
                style={{
                  borderRadius: "10px 10px 10px 10px",
                  backgroundColor: !isTunecore ? highlightColour2 : undefined,
                }}
                className={`${
                  isTunecore
                    ? "bg-tunecore-dark-element text-white"
                    : "text-old-sentric-table-text"
                } hover:shadow-lg ${spotifySuggestionsEnabled ? "lg:w-1/3" : "lg:w-1/2"} w-full h-44 flex flex-col justify-center gap-4 px-6 mb-6 ease-linear transition-all duration-150 hover:scale-105 pointer-events-none`}
              >
                <div className="flex font-semibold text-center rounded">
                  {topSongs?.isLoading
                    ? <LoadingSmall />
                    : (
                        <span className="text-3xl xl:text-4xl">
                          {(topSongs.data?.[0]?.createddate || topSongs.data?.[0]?.createdDate)
                            ? format(
                                Date.parse(topSongs.data?.[0]?.createddate || topSongs.data?.[0]?.createdDate),
                                "yyyy-MM-dd",
                              )
                            : "-"}
                        </span>
                      )}
                </div>
                <p className="text-lg font-medium text-left xl:text-xl ">
                  {t("dashboard:last-song-added", "Last song added")}
                </p>
              </div>
            </div>

            <div
              style={{ borderRadius: "10px 10px 10px 10px" }}
              className="flex flex-row flex-wrap justify-between gap-0 lg:flex-nowrap lg:gap-6"
            >
              <div className="w-full lg:w-1/3">
                <ButtonDashboard
                  id="btnDashboardAddSong"
                  onClick={() => openAccountModal("song")}
                  disabled={!adminRights}
                >
                  {!isTunecore ? (
                    <div className="flex items-center ml-6">
                      <img
                        className="h-4 mr-3"
                        alt="song icon"
                        src={SongIconDark}
                      />
                      {t("dashboard:add-new-song", "Add new song")}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-sm tracking-wider font-tunecore">
                      {t("dashboard:add-new-song", "Add new song")}
                    </div>
                  )}
                </ButtonDashboard>
              </div>
              <div className="w-full lg:w-1/3">
                <ButtonDashboard
                  id="btnDashboardAddWriter"
                  onClick={() => openAccountModal("writer")}
                  disabled={!adminRights}
                >
                  {!isTunecore ? (
                    <div className="flex items-center ml-6">
                      <img
                        className="h-4 mr-3"
                        alt="writer icon"
                        src={WriterIconDark}
                      />
                      {t("dashboard:add-new-writer", "Add new writer")}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-sm tracking-wider font-tunecore">
                      {t("dashboard:add-new-writer", "Add new writer")}
                    </div>
                  )}
                </ButtonDashboard>
              </div>
              <div className="w-full lg:w-1/3">
                {!isTunecore ? (
                  <ButtonDashboard
                    id="btnDashboardAddClaim"
                    onClick={() => openAccountModal("gig")}
                    disabled={!adminRights}
                  >
                    <div className="flex items-center ml-6">
                      <img
                        className="h-4 mr-3"
                        alt="gig icon"
                        src={GigClaimIconDark}
                      />
                      {t("dashboard:create-new-gig-claim", "Create new gig claim")}
                    </div>
                  </ButtonDashboard>
                ) : (
                  <ButtonDashboard
                    id="btnDashboardViewStatments"
                    onClick={viewStatements}
                    disabled={!adminRights}
                  >
                    <div className="flex items-center justify-center text-sm tracking-wider font-tunecore">
                      {t("dashboard:view-statements", "View statements")}
                    </div>
                  </ButtonDashboard>
                )}
              </div>
            </div>

            {!siteConfig?.disable_syncs_and_opportunities_on_dashboard
              && selectedAccount
              && myAccount?.data && (
              <>
                <Spacer />
                <Spacer />
                <H2>{t("dashboard:recent-opportunities-header", "Recent opportunities")}</H2>
                <Spacer />
                <DashboardOpportunitiesBlock />
              </>
            )}

            <div className="flex flex-wrap w-full">
              {siteConfig?.dashboard_box_1
                && siteConfig?.dashboard_box_1.dates && (
                <div className="w-full mt-10 grow lg:w-1/2 lg:pr-4">
                  <div className="h-full p-8 px-8 bg-white rounded-lg html-content db-box">
                    <PortableText value={siteConfig?.dashboard_box_1.dates} />
                  </div>
                </div>
              )}
              {siteConfig?.dashboard_box_2
                && siteConfig?.dashboard_box_2.useful_links && (
                <div className="w-full mt-10 grow lg:w-1/2">
                  <div className="h-full p-8 px-8 space-y-4 bg-white rounded-lg ">

                    <div className="html-content db-box">
                      <PortableText
                        value={siteConfig?.dashboard_box_2.useful_links}
                      />
                    </div>
                    {siteConfig?.dashboard_box_2?.distribution_schedule_pdf?.asset?.url && (
                      <Button
                        variant="primary"
                        label={t("dashboard:current-distribution-schedule", "Current Distribution Schedule")}
                        icon={<IconDownloadFile className="w-5 h-5" stroke="currentColor" />}
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = siteConfig.dashboard_box_2.distribution_schedule_pdf.asset.url;
                          link.download = siteConfig.dashboard_box_2.distribution_schedule_pdf.asset.originalFilename || "Distribution_Schedule.pdf";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      />

                    )}
                  </div>
                </div>
              )}
              <Spacer />
              <Spacer />
              <H2>{t("dashboard:activity-overview-header", "Activity Overview")}</H2>
              <Spacer />
              <TopSongs data={topSongs.data} isLoading={topSongs.isLoading} isError={topSongs.isError} />
              <Spacer />
              <div className="flex flex-wrap w-full gap-8 lg:flex-nowrap">
                <TopWriters data={topWriters.data} isLoading={topWriters.isLoading} isError={topWriters.isError} />
                {!isTunecore && !disableTopGigs && <TopGigs data={topGigs.data} isLoading={topGigs.isLoading} isError={topGigs.isError} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Dashboard;
